import { useState, useEffect, useCallback, Fragment, useMemo } from "react";
import { httpsCallable } from "firebase/functions";
import { functions, carregarMembrosGestao, carregarNucleos, carregarCadastroDoNucleo } from "../firebase";
import {
  opcoesDeCategoria, categoriasDaModalidade, categoriasDisponiveis, chaveCategoria,
} from "../categorias";
import { normalizarModalidade } from "../calculo";
import { idadeEm, ehMaiorDeIdade } from "../idade";
import { CINZA_ESC, TEXTO, PERIGO, INFO_CL, BORDA, BRANCO, RAIO, ESP, FUNDO_SUAVE } from "../tema";
import {
  Card, Btn, Input, Sel, StatusBox, Icone,
  Tabela, Linha, Celula, Etiqueta, Vazio, Carregando, Paginacao,
  PessoaNaLinha, BarraDeFiltros, CampoDeBusca, SeletorDeFiltro, RodapeDeLista,
} from "../componentes";
import useIsMobile from "../useIsMobile";
import SemNucleo from "../componentes/SemNucleo";

// ── Seção: Gerenciar Atletas (cadastro + gestão via Cloud Function) ──
// Idade a partir da data de nascimento. Calculada aqui e nunca gravada:
// idade guardada envelhece errado, e a planilha do programa já tem
// "13 anos" escrito ao lado de nomes que hoje têm 14.
const DIACRITICOS = new RegExp(
  "[" + String.fromCharCode(0x300) + "-" + String.fromCharCode(0x36f) + "]",
  "g"
);

const idadeDe = idadeEm;

// Convenção de escrita da categoria, mostrada ao lado do campo. Vale mais
// que validação: quem cadastra precisa saber a regra ANTES de digitar, e
// não depois de já ter criado a duplicata.
// Vínculos mais comuns. Lista aberta, porque avó, tia e guarda judicial
// aparecem de verdade e recusar o que está fora da lista faria alguém
// inventar um valor qualquer para conseguir salvar.
const PARENTESCOS = [
  "Mãe", "Pai", "Avó", "Avô", "Tia", "Tio",
  "Irmã", "Irmão", "Madrasta", "Padrasto",
  "Tutor legal", "Guardião legal", "Outro",
];

// Mesma conferência do servidor, aqui só para avisar enquanto digita. A
// que vale é a de lá; esta evita a viagem até o servidor para descobrir
// que faltou um número.
function cpfValido(bruto) {
  const d = String(bruto || "").replace(/[^0-9]/g, "");
  if (d.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(d)) return false;   // 111.111.111-11 e afins passam nos dígitos
  const verificador = (ateOnde) => {
    let soma = 0;
    for (let i = 0; i < ateOnde; i++) soma += Number(d[i]) * (ateOnde + 1 - i);
    const resto = (soma * 10) % 11;
    return resto === 10 ? 0 : resto;
  };
  return verificador(9) === Number(d[9]) && verificador(10) === Number(d[10]);
}

// A tela escreve "Gênero", que é a palavra da lista da coordenação. O campo
// gravado continua sendo `sexo`, porque é ele que entra nas equações de
// composição corporal e de maturação, que são específicas por sexo.
const GENEROS = [
  { value: "", label: "— não informado —" },
  { value: "masculino", label: "Masculino" },
  { value: "feminino", label: "Feminino" },
];

// O ranking é da federação, que ranqueia o estado inteiro. Ele não sai dos
// resultados lançados aqui, que só enxergam os atletas do programa, e por
// isso é digitado. "Sem ranking" é informação diferente de campo vazio: um
// diz que a federação não ranqueia o atleta, o outro que ninguém consultou.
const COMO_ESCREVER_RANKING =
  "Digite a posição (ex.: 2) ou \"sem\" quando a federação não ranqueia o atleta. " +
  "Em branco significa que ninguém consultou ainda.";

const COMO_ESCREVER_CATEGORIA =
  "Escreva sempre do mesmo jeito: gênero, depois a faixa. Ex.: \"Girls 12 anos\", \"Boys 13 anos\", \"Elite Men\". " +
  "Evite abreviar e evite traços a mais, porque \"Boys - 13\" e \"Boys 13 anos\" viram duas categorias na contagem.";

export default function SecaoAtletas({ userNucleo, isAdmin, nivelAcesso }) {
  const isCoordGeral = nivelAcesso === "coordenador_geral";
  const isCoordenador = nivelAcesso === "coordenador";
  // Adm Geral e Coordenador Geral enxergam/escolhem qualquer núcleo.
  const vejoTodosNucleos = isAdmin || isCoordGeral;
  const isMobile = useIsMobile();
  const [nucleoAtivo, setNucleoAtivo] = useState(userNucleo || "");
  // Segue o núcleo escolhido no seletor global do cabeçalho: quando o
  // Coordenador Geral/Admin troca de núcleo lá em cima, esta seção acompanha.
  useEffect(() => {
    if (userNucleo) setNucleoAtivo(userNucleo);
  }, [userNucleo]);
  const VAZIO = {
    name: "", email: "", perfil: "Atleta", modalidade: "",
    // O que o termo de consentimento facial precisa. Preenchido aqui, o
    // termo sai pronto para assinar; deixado para depois, vira quatro
    // linhas em branco no papel, e o que se escreve à mão não volta para o
    // sistema.
    dataNascimento: "", categoria: "",
    // Identificação do próprio atleta. Vinha da lista de bolsas, que a
    // coordenação mantinha num documento à parte e redigitava a cada
    // prestação de contas.
    sexo: "", cpf: "",
    rankingEstadual: "", rankingNacional: "", rankingAtualizadoEm: "",
    responsavelNome: "", responsavelCpf: "", responsavelParentesco: "", responsavelTelefone: "",
  };
  const [form, setForm] = useState(VAZIO);
  const [salvando, setSalvando] = useState(false);
  // O termo é do responsável por um menor: só aparece para atleta.
  const ehAtleta = (form.perfil || "").toLowerCase().includes("atleta");
  const cpfInvalido = Boolean(form.responsavelCpf.trim()) && !cpfValido(form.responsavelCpf);
  const cpfDoAtletaInvalido = Boolean(form.cpf.trim()) && !cpfValido(form.cpf);
  // Maior de idade responde por si: não há representante legal a cadastrar.
  const maiorDeIdade = ehMaiorDeIdade(form.dataNascimento);
  const [status, setStatus] = useState(null);
  const [membros, setMembros] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [editando, setEditando] = useState(null); // uid em edição
  const [editForm, setEditForm] = useState({
    name: "", email: "", modalidade: "", dataNascimento: "", categoria: "",
    sexo: "", cpf: "",
    rankingEstadual: "", rankingNacional: "", rankingAtualizadoEm: "",
    responsavelNome: "", responsavelCpf: "", responsavelParentesco: "", responsavelTelefone: "",
  });
  const [excluindo, setExcluindo] = useState(null); // uid em confirmação de exclusão
  const [textoConfirma, setTextoConfirma] = useState("");
  const [acaoEmCurso, setAcaoEmCurso] = useState(false);

  // Lista de núcleos para o seletor do admin — vem da coleção "nucleos"
  // (Fase 2), não mais de uma constante fixa. Só ativos aparecem aqui.
  const [nucleos, setNucleos] = useState([]);

  // Modalidades do núcleo ativo — vêm do próprio doc do núcleo (Fase 2,
  // passo 4), não mais derivadas dos atletas já cadastrados. Vale tanto
  // para admin quanto para coordenador (cada um no seu núcleo ativo).
  const [modalidadesNucleo, setModalidadesNucleo] = useState([]);
  // As categorias cadastradas em cada modalidade do núcleo. Enquanto a
  // modalidade não tiver nenhuma, o campo segue sendo digitado à mão.
  const [categoriasNucleo, setCategoriasNucleo] = useState({});

  // Busca e filtros da lista. Um núcleo com oitenta cadastros não se lê
  // rolando: quem procura alguém sabe o nome, e é por aí que deve entrar.
  const [busca, setBusca] = useState("");
  const [filtroPerfil, setFiltroPerfil] = useState("");
  const [filtroSituacao, setFiltroSituacao] = useState("");
  const [menuDaLinha, setMenuDaLinha] = useState(null);

  const PERFIS_BASE = ["Atleta", "Técnico", "Auxiliar Técnico", "Preparador Físico", "Auxiliar de Preparação"];
  // Hierarquia: quem cada nível pode cadastrar.
  //  - Adm Geral: tudo (inclui Coordenador Geral, Coordenador, Coord. Técnico)
  //  - Coordenador Geral: Coordenador + Coordenador Técnico + demais
  //  - Coordenador: Coordenador Técnico + demais (equipe/atletas)
  //  - Coordenador Técnico: só demais (equipe/atletas)
  let PERFIS = PERFIS_BASE;
  if (isAdmin) PERFIS = [...PERFIS_BASE, "Coordenador Técnico", "Coordenador", "Coordenador Geral"];
  else if (isCoordGeral) PERFIS = [...PERFIS_BASE, "Coordenador Técnico", "Coordenador"];
  else if (isCoordenador) PERFIS = [...PERFIS_BASE, "Coordenador Técnico"];
  const PRECISA_MODALIDADE = ["Atleta", "Técnico", "Auxiliar Técnico"];
  const precisaModalidade = PRECISA_MODALIDADE.includes(form.perfil);

  // Rótulo com gênero para exibição APENAS. O valor gravado no campo
  // "perfil" continua sendo o texto original (ex: "Técnico") — só o que o
  // usuário vê no seletor ganha o "(a)". Nada aqui altera o que vai pro
  // Firestore nem o que o classificarAcesso lê.
  const ROTULO_GENERO = {
    "Técnico": "Técnico(a)",
    "Auxiliar Técnico": "Auxiliar Técnico(a)",
    "Preparador Físico": "Preparador(a) Físico(a)",
    "Auxiliar de Preparação": "Auxiliar de Preparação",
    "Coordenador Técnico": "Coordenador(a) Técnico(a)",
    "Coordenador": "Coordenador(a)",
    "Coordenador Geral": "Coordenador(a) Geral",
  };
  const rotuloPerfil = p => ROTULO_GENERO[p] || p;

  const recarregar = useCallback(async () => {
    setCarregando(true);
    try { setMembros(await carregarMembrosGestao(nucleoAtivo)); }
    catch { setMembros([]); }
    finally { setCarregando(false); }
  }, [nucleoAtivo]);

  useEffect(() => { recarregar(); }, [recarregar]);

  useEffect(() => {
    if (!vejoTodosNucleos) return;
    let ativo = true;
    (async () => {
      try {
        const lista = await carregarNucleos();
        if (ativo) setNucleos(lista.filter(n => n.ativo !== false));
      } catch (e) {
        if (ativo) setStatus({ tipo: "erro", msg: "Erro ao carregar núcleos: " + (e.message || "") });
      }
    })();
    return () => { ativo = false; };
  }, [vejoTodosNucleos]);

  // Se o núcleo ativo sumir da lista (foi inativado, ou é a primeira carga),
  // cai para o primeiro núcleo disponível.
  useEffect(() => {
    if (!vejoTodosNucleos || nucleos.length === 0) return;
    const idsValidos = nucleos.map(n => n.id);
    if (!idsValidos.includes(nucleoAtivo)) {
      setNucleoAtivo(idsValidos[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nucleos, vejoTodosNucleos]);

  useEffect(() => {
    let ativoEfeito = true;
    (async () => {
      try {
        const r = await carregarCadastroDoNucleo(nucleoAtivo);
        if (!ativoEfeito) return;
        setModalidadesNucleo(r.modalidades);
        setCategoriasNucleo(r.categoriasPorModalidade);
      } catch {
        if (!ativoEfeito) return;
        setModalidadesNucleo([]);
        setCategoriasNucleo({});
      }
    })();
    return () => { ativoEfeito = false; };
  }, [nucleoAtivo]);

  // Mensagem de status (erro/ok/info) aparece como toast fixo na tela —
  // visível na hora, de qualquer ponto da lista, e some sozinha.
  useEffect(() => {
    if (!status) return;
    const t = setTimeout(() => setStatus(null), status.tipo === "erro" ? 5000 : 3500);
    return () => clearTimeout(t);
  }, [status]);

  function atualizar(campo, valor) {
    setForm(f => {
      const novo = { ...f, [campo]: valor };
      if (campo === "perfil" && !PRECISA_MODALIDADE.includes(valor)) novo.modalidade = "";
      return novo;
    });
  }

  async function cadastrar() {
    if (!form.name.trim() || !form.email.trim()) { setStatus({ tipo: "erro", msg: "Preencha nome e e-mail." }); return; }
    // CPF só é conferido quando foi digitado. Quem cadastra nem sempre tem
    // o documento do responsável na mão, e travar aqui empurraria a pessoa
    // a inventar número para conseguir salvar.
    if (form.responsavelCpf.trim() && !cpfValido(form.responsavelCpf)) {
      setStatus({ tipo: "erro", msg: "CPF do responsável inválido. Confira os números digitados." });
      return;
    }
    if (form.cpf.trim() && !cpfValido(form.cpf)) {
      setStatus({ tipo: "erro", msg: "CPF do atleta inválido. Confira os números digitados." });
      return;
    }
    if (precisaModalidade && !form.modalidade.trim()) { setStatus({ tipo: "erro", msg: "Informe a modalidade para este perfil." }); return; }
    setSalvando(true);
    setStatus({ tipo: "info", msg: "Cadastrando..." });
    try {
      const criar = httpsCallable(functions, "criarUsuario");
      const payload = { name: form.name.trim(), email: form.email.trim(), perfil: form.perfil, modalidade: form.modalidade.trim() };
      // Só para atleta: o termo é do responsável por um menor, e não faz
      // sentido pedir isso de quem é da equipe técnica.
      if (ehAtleta) {
        payload.dataNascimento = form.dataNascimento.trim();
        payload.categoria = form.categoria.trim();
        payload.sexo = form.sexo.trim();
        payload.cpf = form.cpf.trim();
        payload.rankingEstadual = form.rankingEstadual.trim();
        payload.rankingNacional = form.rankingNacional.trim();
        payload.rankingAtualizadoEm = form.rankingAtualizadoEm.trim();
        payload.responsavelNome = form.responsavelNome.trim();
        payload.responsavelCpf = form.responsavelCpf.trim();
        payload.responsavelParentesco = form.responsavelParentesco.trim();
        payload.responsavelTelefone = form.responsavelTelefone.trim();
      }
      if (vejoTodosNucleos) payload.nucleo = nucleoAtivo;
      const res = await criar(payload);
      const slug = res?.data?.slug || "";
      setStatus({ tipo: "ok", msg: `✅ ${form.name} cadastrado. Identificador: ${slug}. Senha inicial: 123456.` });
      setForm(VAZIO);
      await recarregar();
    } catch (e) {
      setStatus({ tipo: "erro", msg: e?.message || "Erro ao cadastrar." });
    } finally { setSalvando(false); }
  }

  async function alternarAtivo(m) {
    setAcaoEmCurso(true);
    setStatus(null);
    try {
      const fn = httpsCallable(functions, "definirAtivo");
      await fn({ uid: m.uid, ativo: !m.ativo });
      await recarregar();
    } catch (e) {
      setStatus({ tipo: "erro", msg: e?.message || "Erro ao alterar." });
    } finally { setAcaoEmCurso(false); }
  }

  function abrirEdicao(m) {
    setEditando(m.uid);
    setEditForm({
      name: m.nome, email: m.email, modalidade: m.modalidade,
      dataNascimento: m.dataNascimento || "", categoria: m.categoria || "",
      sexo: m.sexo || "", cpf: m.cpf || "",
      rankingEstadual: m.rankingEstadual || "",
      rankingNacional: m.rankingNacional || "",
      rankingAtualizadoEm: m.rankingAtualizadoEm || "",
      responsavelNome: m.responsavelNome || "",
      responsavelCpf: m.responsavelCpf || "",
      responsavelParentesco: m.responsavelParentesco || "",
      responsavelTelefone: m.responsavelTelefone || "",
    });
    setStatus(null);
  }

  // Só acusa depois que a pessoa começou a digitar: campo vazio ainda não
  // é erro, é campo por preencher.
  const cpfPendente = Boolean(editForm.responsavelCpf) && !cpfValido(editForm.responsavelCpf);
  const cpfDoAtletaPendente = Boolean(editForm.cpf) && !cpfValido(editForm.cpf);

  async function salvarEdicao(m) {
    setAcaoEmCurso(true);
    try {
      const fn = httpsCallable(functions, "editarUsuario");
      await fn({
        uid: m.uid, name: editForm.name, email: editForm.email, modalidade: editForm.modalidade,
        dataNascimento: editForm.dataNascimento, categoria: editForm.categoria,
        sexo: editForm.sexo, cpf: editForm.cpf,
        rankingEstadual: editForm.rankingEstadual,
        rankingNacional: editForm.rankingNacional,
        rankingAtualizadoEm: editForm.rankingAtualizadoEm,
        responsavelNome: editForm.responsavelNome,
        responsavelCpf: editForm.responsavelCpf,
        responsavelParentesco: editForm.responsavelParentesco,
        responsavelTelefone: editForm.responsavelTelefone,
      });
      setEditando(null);
      await recarregar();
      setStatus({ tipo: "ok", msg: "Dados atualizados." });
    } catch (e) {
      setStatus({ tipo: "erro", msg: e?.message || "Erro ao salvar." });
    } finally { setAcaoEmCurso(false); }
  }

  async function confirmarExclusao(m) {
    if (textoConfirma.trim().toLowerCase() !== m.nome.trim().toLowerCase()) {
      setStatus({ tipo: "erro", msg: "O nome digitado não confere. Exclusão cancelada." });
      return;
    }
    setAcaoEmCurso(true);
    try {
      const fn = httpsCallable(functions, "excluirUsuario");
      await fn({ uid: m.uid, confirmar: true });
      setExcluindo(null);
      setTextoConfirma("");
      await recarregar();
      setStatus({ tipo: "ok", msg: `${m.nome} excluído definitivamente.` });
    } catch (e) {
      setStatus({ tipo: "erro", msg: e?.message || "Erro ao excluir." });
    } finally { setAcaoEmCurso(false); }
  }

  const ativos = membros.filter(m => m.ativo);
  const inativos = membros.filter(m => !m.ativo);

  // Agrupa os membros por modalidade — grupos sempre visíveis, sem clique.
  // Quem não tem modalidade (coordenação, administrativo) forma um grupo
  // próprio no fim. Dentro de cada grupo: ativos primeiro, inativos depois.
  // As categorias que o campo sugere, para ninguém inventar a própria
  // grafia. Saem da mesma regra do lançamento de resultado, e por isso só
  // trazem o que é DA MODALIDADE do atleta em edição: antes a lista
  // juntava a categoria de qualquer membro do núcleo, e um atleta de
  // tênis de mesa fazia o campo do BMX sugerir Sub-19.
  // O id do datalist por modalidade: dois campos abertos em modalidades
  // diferentes não podem dividir a mesma lista de sugestões.
  const chaveDaModalidade = (m) => chaveCategoria(m) || "sem";

  const emUsoNoNucleo = useMemo(
    () => (membros || []).map((m) => ({ categoria: m.categoria, modalidade: m.modalidade })),
    [membros],
  );
  const sugestoesDaModalidade = (modalidadeDoAtleta) => categoriasDisponiveis({
    mapa: categoriasNucleo,
    modalidade: modalidadeDoAtleta,
    emUso: emUsoNoNucleo,
  });

  // Os filtros valem antes do agrupamento: o grupo de uma modalidade que
  // ficou sem ninguém não aparece, em vez de aparecer vazio.
  const semAcento = (x) => String(x || "").normalize("NFD").replace(DIACRITICOS, "").toLowerCase();
  const membrosFiltrados = membros.filter(m => {
    if (filtroPerfil && m.perfil !== filtroPerfil) return false;
    if (filtroSituacao === "ativos" && !m.ativo) return false;
    if (filtroSituacao === "inativos" && m.ativo) return false;
    if (!busca.trim()) return true;
    const alvo = semAcento(busca.trim());
    return semAcento(m.nome).includes(alvo) || semAcento(m.email).includes(alvo);
  });
  const filtrando = Boolean(busca.trim() || filtroPerfil || filtroSituacao);

  const SEM_MODALIDADE = "Coordenação e equipe (sem modalidade)";
  const gruposPorModalidade = (() => {
    const mapa = {};
    membrosFiltrados.forEach(m => {
      // Normaliza a modalidade na hora de agrupar — cadastros antigos podem
      // ter grafias diferentes ("Bmx", "bmx", "Tenis de Mesa") e não podem
      // virar grupos separados. A exibição usa a mesma forma canônica do
      // seletor de edição (normalizarModalidade, calculo.js).
      const chave = m.modalidade ? normalizarModalidade(m.modalidade) : SEM_MODALIDADE;
      if (!mapa[chave]) mapa[chave] = [];
      mapa[chave].push(m);
    });
    return Object.entries(mapa)
      .sort(([a], [b]) => {
        if (a === SEM_MODALIDADE) return 1;
        if (b === SEM_MODALIDADE) return -1;
        return a.localeCompare(b, "pt-BR");
      })
      .map(([modalidade, lista]) => ({
        modalidade,
        ativos: lista.filter(m => m.ativo),
        inativos: lista.filter(m => !m.ativo),
      }));
  })();

  // Espelha a hierarquia da Cloud Function (não substitui a validação do
  // servidor, só evita mostrar botões que vão dar erro de permissão).
  // Admin gerencia todo mundo; Coordenador Geral gerencia Coordenador
  // Técnico e demais; Coordenador Técnico só gerencia os demais perfis.
  function nivelDoPerfilLocal(perfil) {
    const p = (perfil || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    if (p.includes("admin")) return 4;
    if (p.includes("coorden") && p.includes("geral")) return 3;
    if (p.includes("coorden") && p.includes("tecnico")) return 1;
    if (p.includes("coorden")) return 2;
    return 0;
  }
  const meuNivel = { admin: 4, coordenador_geral: 3, coordenador: 2, coordenador_tecnico: 1 }[nivelAcesso] || 0;
  // Adm Geral e Coordenador Geral gerenciam qualquer um abaixo; os demais só
  // quem tem nível estritamente menor.
  function podeGerenciar(m) {
    return vejoTodosNucleos ? nivelDoPerfilLocal(m.perfil) < meuNivel : meuNivel > nivelDoPerfilLocal(m.perfil);
  }

  // As ações da linha, atrás de um botão só.
  //
  // Antes eram três botões coloridos repetidos em cada linha, e numa lista
  // de quarenta pessoas a coluna inteira virava um paredão de cor que
  // disputava atenção com a situação do cadastro, que é o que se procura.
  function MenuDaLinha({ membro }) {
    const aberto = menuDaLinha === membro.uid;
    const item = (perigo) => ({
      display: "flex", alignItems: "center", gap: 9, width: "100%", textAlign: "left",
      background: "none", border: "none", font: "inherit", fontSize: 13,
      color: perigo ? PERIGO : TEXTO, padding: "10px 14px", cursor: "pointer",
    });
    return (
      <span style={{ position: "relative", display: "inline-block" }}>
        <button
          type="button" aria-label={`Ações de ${membro.nome}`} disabled={acaoEmCurso}
          onClick={() => setMenuDaLinha(aberto ? null : membro.uid)}
          style={{
            background: aberto ? "#EEF2F7" : "none", border: "none", borderRadius: RAIO.sm,
            padding: 6, cursor: acaoEmCurso ? "not-allowed" : "pointer", color: CINZA_ESC,
            display: "inline-flex",
          }}
        >
          <Icone nome="reticencias" tamanho={17} />
        </button>
        {aberto && (
          <>
            <span onClick={() => setMenuDaLinha(null)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
            <span style={{
              position: "absolute", top: "100%", right: 0, zIndex: 50, minWidth: 180,
              background: BRANCO, border: `1px solid ${BORDA}`, borderRadius: RAIO.md,
              boxShadow: "0 6px 20px rgba(16, 24, 40, 0.12)", overflow: "hidden",
              display: "flex", flexDirection: "column",
            }}>
              <button type="button" style={item(false)}
                onClick={() => { setMenuDaLinha(null); abrirEdicao(membro); }}>
                <Icone nome="prancheta" tamanho={15} /> Editar cadastro
              </button>
              <button type="button" style={{ ...item(false), borderTop: `1px solid #F1F5F9` }}
                onClick={() => { setMenuDaLinha(null); alternarAtivo(membro); }}>
                <Icone nome={membro.ativo ? "sair" : "check"} tamanho={15} />
                {membro.ativo ? "Inativar acesso" : "Reativar acesso"}
              </button>
              {vejoTodosNucleos && (
                <button type="button" style={{ ...item(true), borderTop: `1px solid #F1F5F9` }}
                  onClick={() => { setMenuDaLinha(null); setExcluindo(membro.uid); setTextoConfirma(""); setStatus(null); }}>
                  <Icone nome="reticencias" tamanho={15} style={{ opacity: 0 }} /> Excluir definitivamente
                </button>
              )}
            </span>
          </>
        )}
      </span>
    );
  }

  // Sem núcleo escolhido, a tela diz isso em vez de abrir o cadastro
  // de outro núcleo. Depois dos hooks, de propósito.
  if (!nucleoAtivo) return <SemNucleo oQueFaria="ver os atletas e a equipe deste núcleo" />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {status && (
        <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 1000, minWidth: 280, maxWidth: "92vw", boxShadow: "0 6px 20px rgba(0,0,0,0.18)", borderRadius: 10 }}>
          <StatusBox tipo={status.tipo} msg={status.msg} />
        </div>
      )}
      {/* O seletor de núcleo do Coordenador Geral/Admin agora é o global (no
          cabeçalho) — aqui não repetimos. O coordenador comum vê apenas o
          indicador do seu núcleo (travado). */}
      {!vejoTodosNucleos && (
        <Card style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: CINZA_ESC }}>Núcleo</div>
          <div style={{
            border: `1px solid ${BORDA}`, borderRadius: RAIO.md, padding: "9px 14px",
            fontSize: 14, fontWeight: 700, color: TEXTO, background: FUNDO_SUAVE,
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <Icone nome="predio" tamanho={16} /> {userNucleo}
          </div>
        </Card>
      )}

      {/* Novo cadastro */}
      <Card titulo="Novo cadastro" sub="O acesso vale para o portal e para o aplicativo.">
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <Input label="Nome completo" value={form.name} onChange={v => atualizar("name", v)} placeholder="Ex: Maria Nogueira" />
          <Input label="E-mail" value={form.email} onChange={v => atualizar("email", v)} type="email" placeholder="email@instituicao.br" />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <Sel label="Perfil" value={form.perfil} onChange={v => atualizar("perfil", v)} options={PERFIS.map(p => ({ value: p, label: rotuloPerfil(p) }))} />
          {precisaModalidade && (
            modalidadesNucleo.length > 0 ? (
              <Sel label="Modalidade" value={form.modalidade} onChange={v => atualizar("modalidade", v)}
                options={[{ value: "", label: "— selecione —" }, ...modalidadesNucleo]} />
            ) : (
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: CINZA_ESC, display: "block", marginBottom: 5 }}>Modalidade</label>
                <div style={{ padding: "9px 12px", background: "#FEF3C7", borderRadius: 10, fontSize: 12.5, color: "#92400E" }}>
                  Nenhuma modalidade cadastrada para o núcleo {nucleoAtivo}. Cadastre em "Núcleos" primeiro.
                </div>
              </div>
            )
          )}
        </div>
        {/* O bloco do termo, só para atleta. Nada aqui é obrigatório: o
            que ficar em branco continua podendo entrar pela edição, e o
            termo aponta o que falta quando for emitido. */}
        {ehAtleta && (
          <div style={{ borderTop: `1px solid ${BORDA}`, paddingTop: 14, marginBottom: 14 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: TEXTO, marginBottom: 3 }}>
              Dados do atleta
            </div>
            <div style={{ fontSize: 12.5, color: CINZA_ESC, marginBottom: 12, lineHeight: 1.55 }}>
              Preenchidos agora, a ficha do atleta e o termo de consentimento saem prontos.
              Podem ficar para depois, pela edição do cadastro.
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <Input label="Data de nascimento" type="date" value={form.dataNascimento}
                onChange={v => atualizar("dataNascimento", v)} />
              <Sel label="Gênero" value={form.sexo}
                onChange={v => atualizar("sexo", v)} options={GENEROS} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <Input label="CPF do atleta" value={form.cpf}
                onChange={v => atualizar("cpf", v)} placeholder="000.000.000-00" />
              {/* A categoria do cadastro é a PRINCIPAL, a da idade do
                  atleta. Quando ele disputa outra no mesmo evento, a
                  secundária é escolhida no lançamento do resultado.
                  A lista vem da modalidade, cadastrada em Núcleos; sem
                  lista ali, o campo continua livre. */}
              {categoriasDaModalidade(categoriasNucleo, form.modalidade).length > 0 ? (
                <Sel label="Categoria principal" value={form.categoria}
                  onChange={v => atualizar("categoria", v)}
                  options={[
                    { value: "", label: "— escolha —" },
                    ...opcoesDeCategoria(categoriasNucleo, form.modalidade, form.categoria)
                      .map(c => ({ value: c, label: c })),
                  ]} />
              ) : (
                <Input label="Categoria principal" value={form.categoria}
                  onChange={v => atualizar("categoria", v)} placeholder="Ex: Boys 13 anos" />
              )}
            </div>
            {cpfDoAtletaInvalido && (
              <div style={{ fontSize: 12.5, color: PERIGO, marginBottom: 12, fontWeight: 600 }}>
                CPF do atleta inválido. Confira os números digitados.
              </div>
            )}
            {/* O ranking aparece só na ficha do atleta, em Desempenho. Ele é
                digitado aqui porque é aqui que o cadastro do atleta se faz,
                e não se repete em duas telas. */}
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 12 }}>
              <Input label="Ranking estadual" value={form.rankingEstadual}
                onChange={v => atualizar("rankingEstadual", v)} placeholder="Ex: 2" />
              <Input label="Ranking nacional" value={form.rankingNacional}
                onChange={v => atualizar("rankingNacional", v)} placeholder="Ex: 13" />
              <Input label="Ranking consultado em" type="date" value={form.rankingAtualizadoEm}
                onChange={v => atualizar("rankingAtualizadoEm", v)} />
            </div>
            <div style={{ fontSize: 12, color: CINZA_ESC, margin: "7px 0 14px", lineHeight: 1.55 }}>
              {COMO_ESCREVER_RANKING}
            </div>
            <div style={{ borderTop: `1px solid ${BORDA}`, paddingTop: 14, marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: TEXTO, marginBottom: 3 }}>
                Termo de consentimento facial
              </div>
              <div style={{ fontSize: 12.5, color: CINZA_ESC, marginBottom: 12, lineHeight: 1.55 }}>
                Quem assina pelo atleta.
              </div>
            </div>
            {/* Maior de idade assina o próprio termo: não há representante
                legal, e pedir o CPF de um responsável a quem responde por si
                seria coletar dado de terceiro sem finalidade. Fica o
                telefone, que é dele. */}
            {maiorDeIdade ? (
              <>
                <div style={{
                  background: "#DCFCE7", border: "1px solid #04785733", borderRadius: 8,
                  padding: "10px 12px", fontSize: 12.5, color: "#047857", lineHeight: 1.6, marginBottom: 12,
                }}>
                  <strong>Maior de idade.</strong> O próprio atleta assina o termo de consentimento
                  facial, e os dados de responsável legal não são necessários.
                </div>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
                  <Input label="Telefone de contato" value={form.responsavelTelefone}
                    onChange={v => atualizar("responsavelTelefone", v)} placeholder="(85) 90000-0000" />
                </div>
              </>
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12, marginBottom: 12 }}>
                  <Input label="Nome do responsável legal" value={form.responsavelNome}
                    onChange={v => atualizar("responsavelNome", v)} placeholder="Quem assina o termo" />
                  <Input label="CPF do responsável" value={form.responsavelCpf}
                    onChange={v => atualizar("responsavelCpf", v)} placeholder="000.000.000-00" />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
                  <Input label="Parentesco ou vínculo" value={form.responsavelParentesco}
                    onChange={v => atualizar("responsavelParentesco", v)} placeholder="Ex: mãe, pai, avó" />
                  <Input label="Telefone de contato" value={form.responsavelTelefone}
                    onChange={v => atualizar("responsavelTelefone", v)} placeholder="(85) 90000-0000" />
                </div>
              </>
            )}
            {cpfInvalido && (
              <div style={{ fontSize: 12.5, color: PERIGO, marginTop: 8, fontWeight: 600 }}>
                CPF inválido. Confira os números digitados.
              </div>
            )}
          </div>
        )}

        <div style={{ background: INFO_CL, borderRadius: 8, padding: "10px 12px", fontSize: 12.5, color: "#1e3a5f", lineHeight: 1.6, marginBottom: 14 }}>
          O usuário nasce com a senha padrão <strong>123456</strong> e troca no primeiro acesso. O identificador é gerado automaticamente.
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Btn onClick={cadastrar} disabled={salvando} icone={salvando ? undefined : "mais"}>{salvando ? "Cadastrando…" : "Cadastrar usuário"}</Btn>
        </div>
      </Card>

      {/* Lista de membros com gestão */}
      <Card
        titulo={`Membros do núcleo ${nucleoAtivo}`}
        sub={carregando
          ? "Carregando os cadastros…"
          : `${ativos.length} ativo${ativos.length !== 1 ? "s" : ""}${inativos.length ? ` · ${inativos.length} inativo${inativos.length !== 1 ? "s" : ""}` : ""}`}
      >
        <BarraDeFiltros style={{ marginBottom: ESP.lg }}>
          <CampoDeBusca valor={busca} onChange={setBusca} placeholder="Buscar por nome ou e-mail..." />
          <SeletorDeFiltro
            valor={filtroPerfil} onChange={setFiltroPerfil} largura={200}
            opcoes={[{ value: "", label: "Todos os perfis" }, ...PERFIS.map(x => ({ value: x, label: rotuloPerfil(x) }))]}
          />
          <SeletorDeFiltro
            valor={filtroSituacao} onChange={setFiltroSituacao} largura={180}
            opcoes={[
              { value: "", label: "Ativos e inativos" },
              { value: "ativos", label: "Somente ativos" },
              { value: "inativos", label: "Somente inativos" },
            ]}
          />
        </BarraDeFiltros>

        {carregando ? (
          <Carregando linhas={5} />
        ) : membros.length === 0 ? (
          <Vazio titulo="Nenhum membro cadastrado ainda">
            Use o formulário acima para criar o primeiro acesso deste núcleo.
          </Vazio>
        ) : membrosFiltrados.length === 0 ? (
          <Vazio titulo="Nenhum cadastro encontrado">
            Não há ninguém com esse nome, perfil ou situação neste núcleo. Limpe a busca para ver a lista inteira.
          </Vazio>
        ) : (
          <Tabela colunas={[
            { rotulo: "Nome", alinha: "left" },
            { rotulo: "Perfil", alinha: "left" },
            { rotulo: "Categoria", alinha: "left" },
            { rotulo: "Situação", alinha: "left" },
            { rotulo: "Ações", alinha: "right" },
          ]}>
            {gruposPorModalidade.map(g => (
              <Fragment key={g.modalidade}>
                {/* A modalidade continua sendo o agrupamento da lista. Como
                    faixa dentro da tabela, e não como tabela separada: as
                    colunas continuam alinhadas de um grupo para o outro. */}
                <tr>
                  <td colSpan={5} style={{
                    background: FUNDO_SUAVE, borderTop: `1px solid ${BORDA}`, borderBottom: `1px solid ${BORDA}`,
                    padding: "9px 12px", textAlign: "left",
                  }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: TEXTO, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      {g.modalidade}
                    </span>
                    <span style={{ fontSize: 11.5, color: CINZA_ESC, marginLeft: 10, fontWeight: 500 }}>
                      {g.ativos.length} ativo{g.ativos.length !== 1 ? "s" : ""}
                      {g.inativos.length > 0 ? ` · ${g.inativos.length} inativo${g.inativos.length !== 1 ? "s" : ""}` : ""}
                    </span>
                  </td>
                </tr>

                {[...g.ativos, ...g.inativos].map(m => (
                  editando === m.uid ? (
                    <tr key={m.uid}>
                      <td colSpan={5} style={{ padding: "16px 14px", background: "#F8FAFF", borderBottom: `1px solid ${BORDA}` }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
                          <Input label="Nome" value={editForm.name} onChange={v => setEditForm(f => ({ ...f, name: v }))} />
                          <Input label="E-mail" value={editForm.email} onChange={v => setEditForm(f => ({ ...f, email: v }))} type="email" />
                        </div>
                        {PRECISA_MODALIDADE.includes(m.perfil) && (
                          modalidadesNucleo.length > 0 ? (
                            <Sel label="Modalidade" value={editForm.modalidade} onChange={v => setEditForm(f => ({ ...f, modalidade: v }))}
                              options={[{ value: "", label: "— selecione —" }, ...modalidadesNucleo]} />
                          ) : (
                            <div style={{ padding: "9px 12px", background: "#FEF3C7", borderRadius: 10, fontSize: 12.5, color: "#92400E" }}>
                              Nenhuma modalidade cadastrada para o núcleo {nucleoAtivo}.
                            </div>
                          )
                        )}
                        {String(m.perfil || "").toLowerCase().includes("atleta") && (
                          <>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                              <div>
                                <div style={{ fontSize: 11, color: "#64748B", fontWeight: 700, textTransform: "uppercase", marginBottom: 5 }}>
                                  Data de nascimento
                                </div>
                                <input type="date" value={editForm.dataNascimento}
                                  onChange={e => setEditForm(f => ({ ...f, dataNascimento: e.target.value }))}
                                  style={{ width: "100%", border: "1px solid #D1D5DB", borderRadius: 10, padding: "9px 11px", fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" }} />
                              </div>
                              <div>
                                <div style={{ fontSize: 11, color: "#64748B", fontWeight: 700, textTransform: "uppercase", marginBottom: 5 }}>
                                  Idade
                                </div>
                                <div style={{ padding: "9px 11px", background: "#EAF0FA", borderRadius: 10, fontSize: 13, fontWeight: 700, color: "#24417D" }}>
                                  {idadeDe(editForm.dataNascimento) === null
                                    ? "—"
                                    : idadeDe(editForm.dataNascimento) + " anos"}
                                </div>
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: 11, color: "#64748B", fontWeight: 700, textTransform: "uppercase", marginBottom: 5 }}>
                                Categoria principal
                              </div>
                              {/* Escolhida da lista da modalidade quando ela
                                  tem lista. A categoria que o atleta já tem
                                  entra como opção mesmo se a coordenação a
                                  tirou depois: sem isso o campo abriria
                                  vazio e salvar apagaria a que ele tinha. */}
                              {categoriasDaModalidade(categoriasNucleo, editForm.modalidade).length > 0 ? (
                                <select value={editForm.categoria}
                                  onChange={e => setEditForm(f => ({ ...f, categoria: e.target.value }))}
                                  style={{ width: "100%", border: "1px solid #D1D5DB", borderRadius: 10, padding: "9px 11px", fontSize: 13, fontFamily: "inherit", boxSizing: "border-box", background: "#FFF", cursor: "pointer" }}>
                                  <option value="">— escolha —</option>
                                  {opcoesDeCategoria(categoriasNucleo, editForm.modalidade, editForm.categoria)
                                    .map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                              ) : (
                                <>
                                  <input list={`cats-em-uso-${chaveDaModalidade(editForm.modalidade)}`} value={editForm.categoria}
                                    onChange={e => setEditForm(f => ({ ...f, categoria: e.target.value }))}
                                    placeholder="Ex: Girls 12 anos"
                                    style={{ width: "100%", border: "1px solid #D1D5DB", borderRadius: 10, padding: "9px 11px", fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" }} />
                                  <datalist id={`cats-em-uso-${chaveDaModalidade(editForm.modalidade)}`}>
                                    {sugestoesDaModalidade(editForm.modalidade).map(c => <option key={c} value={c} />)}
                                  </datalist>
                                  <div style={{ fontSize: 11.5, color: "#64748B", marginTop: 6, lineHeight: 1.5 }}>
                                    {COMO_ESCREVER_CATEGORIA}
                                  </div>
                                </>
                              )}
                              {/* Onde a lista existe, a tela diz de onde ela
                                  vem: quem não achar a categoria do atleta
                                  precisa saber onde ela se cadastra. */}
                              {categoriasDaModalidade(categoriasNucleo, editForm.modalidade).length > 0 && (
                                <div style={{ fontSize: 11.5, color: "#64748B", marginTop: 6, lineHeight: 1.5 }}>
                                  As categorias de {editForm.modalidade} se cadastram em Núcleos.
                                  Aqui entra a principal; a outra que ele disputar no mesmo evento
                                  é escolhida no lançamento do resultado.
                                </div>
                              )}
                            </div>

                            {/* Gênero e CPF do próprio atleta. O gênero decide
                                qual equação de composição corporal e de maturação
                                roda na avaliação física, então o campo vazio é
                                cobrado na ficha em vez de assumir um padrão. */}
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                              <div>
                                <div style={{ fontSize: 11, color: "#64748B", fontWeight: 700, textTransform: "uppercase", marginBottom: 5 }}>Gênero</div>
                                <select value={editForm.sexo}
                                  onChange={e => setEditForm(f => ({ ...f, sexo: e.target.value }))}
                                  style={{ width: "100%", border: "1px solid #D1D5DB", borderRadius: 10, padding: "9px 11px", fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" }}>
                                  {GENEROS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                                </select>
                              </div>
                              <div>
                                <div style={{ fontSize: 11, color: "#64748B", fontWeight: 700, textTransform: "uppercase", marginBottom: 5 }}>CPF do atleta</div>
                                <input value={editForm.cpf}
                                  onChange={e => setEditForm(f => ({ ...f, cpf: e.target.value }))}
                                  placeholder="000.000.000-00" style={{ width: "100%", border: "1px solid #D1D5DB", borderRadius: 10, padding: "9px 11px", fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" }} />
                                {cpfDoAtletaPendente && (
                                  <div style={{ fontSize: 11.5, color: PERIGO, marginTop: 5, fontWeight: 600 }}>
                                    CPF inválido.
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* O ranking é da federação e aparece na ficha do
                                atleta, em Desempenho. Fica aqui porque é aqui que
                                o cadastro se faz, e dado digitado em duas telas
                                diverge na primeira vez que alguém corrige só uma. */}
                            <div style={{ borderTop: "1px solid #E5E7EB", paddingTop: 14 }}>
                              <div style={{ fontSize: 11, color: "#64748B", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
                                Ranking da federação
                              </div>
                              <div style={{ fontSize: 11.5, color: "#64748B", marginTop: 4, marginBottom: 10, lineHeight: 1.5 }}>
                                {COMO_ESCREVER_RANKING}
                              </div>
                              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                                <div>
                                  <div style={{ fontSize: 11, color: "#64748B", fontWeight: 700, textTransform: "uppercase", marginBottom: 5 }}>Estadual</div>
                                  <input value={editForm.rankingEstadual}
                                    onChange={e => setEditForm(f => ({ ...f, rankingEstadual: e.target.value }))}
                                    placeholder="2" style={{ width: "100%", border: "1px solid #D1D5DB", borderRadius: 10, padding: "9px 11px", fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" }} />
                                </div>
                                <div>
                                  <div style={{ fontSize: 11, color: "#64748B", fontWeight: 700, textTransform: "uppercase", marginBottom: 5 }}>Nacional</div>
                                  <input value={editForm.rankingNacional}
                                    onChange={e => setEditForm(f => ({ ...f, rankingNacional: e.target.value }))}
                                    placeholder="13" style={{ width: "100%", border: "1px solid #D1D5DB", borderRadius: 10, padding: "9px 11px", fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" }} />
                                </div>
                                <div>
                                  <div style={{ fontSize: 11, color: "#64748B", fontWeight: 700, textTransform: "uppercase", marginBottom: 5 }}>Consultado em</div>
                                  <input type="date" value={editForm.rankingAtualizadoEm}
                                    onChange={e => setEditForm(f => ({ ...f, rankingAtualizadoEm: e.target.value }))}
                                    style={{ width: "100%", border: "1px solid #D1D5DB", borderRadius: 10, padding: "9px 11px", fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" }} />
                                </div>
                              </div>
                            </div>

                            {/* Responsável legal. São dados de um terceiro, que não usa o
                                sistema e não escolheu estar nele: entram por uma finalidade
                                só, identificar quem autorizou o tratamento da imagem do
                                menor, e o termo sai preenchido com eles. */}
                            <div style={{ borderTop: "1px solid #E5E7EB", paddingTop: 14 }}>
                              <div style={{ fontSize: 11, color: "#64748B", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
                                Responsável legal
                              </div>
                              <div style={{ fontSize: 11.5, color: "#64748B", marginTop: 4, marginBottom: 10, lineHeight: 1.5 }}>
                                Vai impresso no termo de consentimento, que sai pronto para assinatura.
                                Só a coordenação enxerga estes campos.
                              </div>

                              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                <div>
                                  <div style={{ fontSize: 11, color: "#64748B", fontWeight: 700, textTransform: "uppercase", marginBottom: 5 }}>
                                    Nome completo
                                  </div>
                                  <input value={editForm.responsavelNome}
                                    onChange={e => setEditForm(f => ({ ...f, responsavelNome: e.target.value }))}
                                    placeholder="Como está no documento de identidade"
                                    style={{ width: "100%", border: "1px solid #D1D5DB", borderRadius: 10, padding: "9px 11px", fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" }} />
                                </div>

                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                                  <div>
                                    <div style={{ fontSize: 11, color: "#64748B", fontWeight: 700, textTransform: "uppercase", marginBottom: 5 }}>
                                      CPF
                                    </div>
                                    <input value={editForm.responsavelCpf}
                                      onChange={e => setEditForm(f => ({ ...f, responsavelCpf: e.target.value }))}
                                      placeholder="000.000.000-00" inputMode="numeric"
                                      style={{
                                        width: "100%", borderRadius: 10, padding: "9px 11px", fontSize: 13,
                                        fontFamily: "inherit", boxSizing: "border-box",
                                        border: `1.5px solid ${cpfPendente ? PERIGO : "#D1D5DB"}`,
                                      }} />
                                  </div>
                                  <div>
                                    <div style={{ fontSize: 11, color: "#64748B", fontWeight: 700, textTransform: "uppercase", marginBottom: 5 }}>
                                      Parentesco
                                    </div>
                                    <input list="parentescos" value={editForm.responsavelParentesco}
                                      onChange={e => setEditForm(f => ({ ...f, responsavelParentesco: e.target.value }))}
                                      placeholder="Ex: Mãe"
                                      style={{ width: "100%", border: "1px solid #D1D5DB", borderRadius: 10, padding: "9px 11px", fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" }} />
                                    <datalist id="parentescos">
                                      {PARENTESCOS.map(p => <option key={p} value={p} />)}
                                    </datalist>
                                  </div>
                                </div>

                                <div>
                                  <div style={{ fontSize: 11, color: "#64748B", fontWeight: 700, textTransform: "uppercase", marginBottom: 5 }}>
                                    Telefone com DDD
                                  </div>
                                  <input value={editForm.responsavelTelefone}
                                    onChange={e => setEditForm(f => ({ ...f, responsavelTelefone: e.target.value }))}
                                    placeholder="(85) 99999-0000" inputMode="tel"
                                    style={{ width: "100%", border: "1px solid #D1D5DB", borderRadius: 10, padding: "9px 11px", fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" }} />
                                </div>

                                {cpfPendente && (
                                  <div style={{ fontSize: 12, color: PERIGO, lineHeight: 1.5 }}>
                                    CPF incompleto ou com dígito errado. Confira os números antes de salvar:
                                    CPF errado num termo assinado identifica outra pessoa.
                                  </div>
                                )}
                              </div>
                            </div>
                          </>
                        )}

                        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                          <Btn onClick={() => setEditando(null)} variant="secondary" disabled={acaoEmCurso}>Cancelar</Btn>
                          <Btn onClick={() => salvarEdicao(m)} disabled={acaoEmCurso || cpfPendente}>{acaoEmCurso ? "…" : "Salvar"}</Btn>
                        </div>
                      </div>
                        </div>
                      </td>
                    </tr>
                  ) : excluindo === m.uid ? (
                    <tr key={m.uid}>
                      <td colSpan={5} style={{ padding: "16px 14px", background: "#FEF2F2", borderBottom: `1px solid ${BORDA}` }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <div style={{ fontSize: 13, color: "#991B1B", fontWeight: 600 }}>⚠️ Exclusão definitiva e irreversível</div>
                        <div style={{ fontSize: 12.5, color: "#7F1D1D", lineHeight: 1.6 }}>
                          Isso apaga <strong>{m.nome}</strong> e todo o registro dele do sistema. O recomendado é <strong>inativar</strong> (mantém o histórico). Para confirmar a exclusão, digite o nome do atleta abaixo:
                        </div>
                        <Input label={`Digite "${m.nome}" para confirmar`} value={textoConfirma} onChange={setTextoConfirma} placeholder={m.nome} />
                        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                          <Btn onClick={() => { setExcluindo(null); setTextoConfirma(""); }} variant="secondary" disabled={acaoEmCurso}>Cancelar</Btn>
                          <button onClick={() => confirmarExclusao(m)} disabled={acaoEmCurso} style={{ background: PERIGO, color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontSize: 14, fontWeight: 700, cursor: acaoEmCurso ? "not-allowed" : "pointer" }}>
                            {acaoEmCurso ? "…" : "Excluir definitivamente"}
                          </button>
                        </div>
                      </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <Linha key={m.uid}>
                      <Celula alinha="left">
                        <PessoaNaLinha nome={m.nome} apoio={m.email} />
                      </Celula>
                      <Celula alinha="left" style={{ color: CINZA_ESC, fontSize: 12.5 }}>
                        {rotuloPerfil(m.perfil)}
                      </Celula>
                      <Celula alinha="left" style={{ color: CINZA_ESC, fontSize: 12.5 }}>
                        {m.categoria || "—"}
                      </Celula>
                      <Celula alinha="left">
                        <Etiqueta tom={m.ativo ? "ok" : "neutro"} ponto>
                          {m.ativo ? "Ativo" : "Inativo"}
                        </Etiqueta>
                      </Celula>
                      <Celula alinha="right">
                        {podeGerenciar(m) ? (
                          <MenuDaLinha membro={m} />
                        ) : (
                          <span style={{ fontSize: 11.5, color: "#9CA3AF" }}>sem permissão</span>
                        )}
                      </Celula>
                    </Linha>
                  )
                ))}
              </Fragment>
            ))}
          </Tabela>
        )}

        {!carregando && membrosFiltrados.length > 0 && (
          <RodapeDeLista
            inicio={1} fim={membrosFiltrados.length} total={membrosFiltrados.length}
            unidade={membrosFiltrados.length === 1 ? "usuário" : "usuários"}
          >
            {filtrando && (
              <span style={{ fontSize: 12.5, color: CINZA_ESC }}>
                de {membros.length} no núcleo
              </span>
            )}
            <Paginacao pagina={1} totalPaginas={1} onIr={() => {}} />
          </RodapeDeLista>
        )}

        <div style={{ fontSize: 11.5, color: CINZA_ESC, marginTop: ESP.md, lineHeight: 1.6 }}>
          <strong>Inativar</strong> mantém a pessoa no histórico do sistema, e é o certo para um desligamento.
          <strong> Excluir</strong> apaga tudo definitivamente, e serve só para um cadastro criado errado.
        </div>
      </Card>
    </div>
  );
}
